/**
 * Right-side action buttons for the single-order header:
 * Candid upload, NAR RPA trigger link, three-dot menu, and refresh icon.
 * @module headerActionButtons
 */
import { SpinningLoader } from "risa-oasis-ui_v2";
import MedicalPaThreeDotsMenu from "../../../../../components/threeDotsWithClickableOptions/medicalPaThreeDotsMenu/MedicalPaThreeDotsMenu";
import { Item } from "../../../../../data-model/documentModalWithSideBav";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { Submission } from "../../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { UseHealthCareFacilityData } from "../../../../../hooks/useHealthCareFacility";
import RefreshIcon from "../../../../../svg/refresh-icon";

/**
 * Props for {@link HeaderActionButtons}.
 */
interface HeaderActionButtonsProps {
  shouldShowNarRpaTrigger: boolean;
  isNarRpaInProgress: boolean;
  onNarRpaTriggerClick: () => void;
  shouldShowThreeDots: boolean;
  guidelinesDocuments?: Item[];
  healthCareFacilityData: UseHealthCareFacilityData;
  onRefreshClick: () => void;
  isRefreshing: boolean;
  hideButton?: boolean;
  /** Auth-on-file details for the three-dots menu. */
  authOnFileDetails?: AuthOnFile;
  /** General order details for context in sub-menus. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Submission data for ICD code display in sub-menus. */
  submissionData?: Submission;
  /** Resolved primary payer name forwarded to the Candid upload button. */
  primaryPayerName?: string;
}

/**
 * Renders the Candid upload button, NAR RPA trigger, overflow menu, and
 * refresh control for the single-order header.
 *
 * The Candid upload button is only shown when the facility has the
 * `enable_candid_auth_letter_upload` feature flag enabled and the
 * auth-on-file `auth_status` is one of {@link CANDID_ELIGIBLE_AUTH_STATUSES}.
 *
 * @param props - Action button configuration and handlers
 * @returns Header action button cluster
 */
const HeaderActionButtons = ({
  shouldShowNarRpaTrigger,
  isNarRpaInProgress,
  onNarRpaTriggerClick,
  shouldShowThreeDots,
  guidelinesDocuments,
  healthCareFacilityData,
  onRefreshClick,
  isRefreshing,
  hideButton,
  authOnFileDetails,
  generalOrderDetails,
  submissionData,
  primaryPayerName,
}: HeaderActionButtonsProps) => {
  return (
    <div className="flex flex-row items-center gap-2 self-end">
      {shouldShowNarRpaTrigger && (
        <div
          className={`mr-2 text-sm font-semibold ${
            isNarRpaInProgress
              ? "text-primaryGray-10 opacity-50"
              : "cursor-pointer text-primaryGray-1 hover:text-primaryGray-5 hover:underline"
          }`}
          onClick={() => {
            if (isNarRpaInProgress) return;
            onNarRpaTriggerClick();
          }}
        >
          Trigger NAR RPA
        </div>
      )}
      {!hideButton && (
        <div className="flex flex-row items-center gap-3">
          {shouldShowThreeDots && generalOrderDetails && authOnFileDetails && (
            <div className="ml-auto flex items-center gap-2">
              <MedicalPaThreeDotsMenu
                items={guidelinesDocuments ?? []}
                healthCareFacilityData={healthCareFacilityData}
                authOnFile={authOnFileDetails}
                generalOrderDetails={generalOrderDetails}
                submissionData={submissionData ?? ({} as Submission)}
                screenName={ScreenNamesForBigQuery.AUTH_STATUS_SCREEN}
              />
            </div>
          )}
          <div
            className="refresh-icon flex h-[1.375rem] w-[1.375rem] items-center justify-center rounded-[0.1375rem] bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
            onClick={onRefreshClick}
          >
            {isRefreshing ? <SpinningLoader /> : <RefreshIcon />}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderActionButtons;
